import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import braintree from "braintree-web";
import { Controller, Control, FieldErrors } from "react-hook-form";
import {
  Box,
  FormControl,
  InputLabel,
  FormHelperText,
  Typography,
  Select,
  MenuItem,
  TextField,
} from "./MuiImports";

import { useTranslation } from "react-i18next";
import {
  BRAINTREE_AUTH_TOKEN,
  BRAINTREE_AUTH_TOKEN_BRAZIL,
} from "../../config/configs";
import styles from "./BraintreeHostedFieldsForm.module.css";
import { COUNTRIES } from "../../assets/const/countries.const";
import { validateCPF } from "../../utils/cpfValidator";
import { BRAZIL_AIRPORT_VENUES } from "../../assets/const/brazil-venues.consts";
import { formatCPF } from "../../utils/utils";

interface BraintreeHostedFieldsFormProps {
  control?: Control<any>;
  onValidityChange: (isValid: boolean) => void;
  venue_id?: string;
  isSelfcareForm?: boolean;
}

export interface BraintreeHostedFieldsFormRef {
  tokenize: () => Promise<{
    payload: braintree.HostedFieldsTokenizePayload;
    deviceData: string;
  }>;
  clearFields: () => void;
}

const BraintreeHostedFieldsForm = forwardRef<
  BraintreeHostedFieldsFormRef,
  BraintreeHostedFieldsFormProps
>(({ control, onValidityChange, venue_id, isSelfcareForm = false }, ref) => {
  const { t } = useTranslation();

  // Determine if this is a Brazilian venue (affects token selection only)
  const isBrazilVenue =
    venue_id && BRAZIL_AIRPORT_VENUES.includes(venue_id.toUpperCase());

  const [countryCode, setCountryCode] = useState(() => {
    if (isBrazilVenue) {
      return "BR";
    }
    return "US";
  });
  const [hostedFieldsInstance, setHostedFieldsInstance] =
    useState<braintree.HostedFields | null>(null);
  const [dataCollectorInstance, setDataCollectorInstance] =
    useState<braintree.DataCollector | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isValid, setIsValid] = useState(false);
  const [fieldStates, setFieldStates] = useState({
    number: { isFocused: false, isEmpty: true },
    expirationDate: { isFocused: false, isEmpty: true },
    cvv: { isFocused: false, isEmpty: true },
    postalCode: { isFocused: false, isEmpty: true },
  });
  const [isTearingDown, setIsTearingDown] = useState(false);

  useImperativeHandle(ref, () => ({
    tokenize: async () => {
      if (!hostedFieldsInstance || isTearingDown) {
        throw new Error("Hosted fields are not valid or not initialized");
      }

      try {
        const payload = await hostedFieldsInstance.tokenize();
        let deviceData = "";

        try {
          if (dataCollectorInstance && dataCollectorInstance.deviceData) {
            deviceData = dataCollectorInstance.deviceData;
          }
        } catch (deviceDataError) {
          console.warn("Could not get device data:", deviceDataError);
        }

        return { payload, deviceData };
      } catch (error) {
        console.error("Error tokenizing payment method:", error);
        throw error;
      }
    },
    clearFields: () => {
      if (hostedFieldsInstance && !isTearingDown) {
        hostedFieldsInstance.clear("number");
        hostedFieldsInstance.clear("expirationDate");
        hostedFieldsInstance.clear("cvv");
        hostedFieldsInstance.clear("postalCode");
        setFieldStates({
          number: { isFocused: false, isEmpty: true },
          expirationDate: { isFocused: false, isEmpty: true },
          cvv: { isFocused: false, isEmpty: true },
          postalCode: { isFocused: false, isEmpty: true },
        });
        setErrors({});
        setIsValid(false);
        onValidityChange(false);
      }
    },
  }));

  useEffect(() => {
    initializeBraintreeFields();

    return () => {
      if (hostedFieldsInstance && !isTearingDown) {
        hostedFieldsInstance.teardown().catch(console.error);
      }
    };
  }, []);

  // Reinitialize fields when country changes (to add/remove postal code field)
  // Token remains venue-based, but field configuration changes
  useEffect(() => {
    const reinitializeFields = async () => {
      if (hostedFieldsInstance && !isTearingDown) {
        try {
          setIsTearingDown(true);
          await hostedFieldsInstance.teardown();
          setHostedFieldsInstance(null);
          await initializeBraintreeFields();
        } catch (error) {
          console.error("Error reinitializing hosted fields:", error);
        } finally {
          setIsTearingDown(false);
        }
      }
    };

    if (hostedFieldsInstance) {
      reinitializeFields();
    }
  }, [countryCode]);

  const initializeBraintreeFields = async () => {
    if (isTearingDown) return;

    try {
      // Token selection is based on venue, not user-selected country
      const clientToken = isBrazilVenue
        ? `${BRAINTREE_AUTH_TOKEN_BRAZIL}`
        : `${BRAINTREE_AUTH_TOKEN}`;

      if (!clientToken) {
        console.error("No client token available");
        return;
      }

      // Create client instance and wait for it to be ready
      const clientInstance = await braintree.client.create({
        authorization: clientToken,
      });

      // Ensure client instance is valid before proceeding
      if (!clientInstance) {
        console.error("Failed to create Braintree client instance");
        return;
      }

      // Initialize Data Collector with more specific error handling
      try {
        const dataCollectorConfig = {
          client: clientInstance,
          kount: false, // First try without Kount
        };

        const dataCollector = await braintree.dataCollector.create(
          dataCollectorConfig
        );

        if (dataCollector) {
          console.log("Data Collector instance created successfully");
          setDataCollectorInstance(dataCollector);

          // After basic collector works, try enabling Kount
          try {
            const kountCollector = await braintree.dataCollector.create({
              client: clientInstance,
              kount: true,
            });

            if (kountCollector) {
              console.log("Kount Data Collector instance created successfully");
              setDataCollectorInstance(kountCollector);
            }
          } catch (kountError: any) {
            console.warn("Kount collection disabled:", kountError.message);
            // Keep using the basic collector that was already created
          }
        }
      } catch (dataCollectorError: any) {
        console.error("Data Collector Error:", {
          message: dataCollectorError.message,
          code: dataCollectorError.code,
          type: dataCollectorError.type,
          details: dataCollectorError.details,
        });

        // Continue without Data Collector
        console.warn("Continuing without Data Collector");
      }

      const fields: any = {
        number: {
          selector: "#card-number",
        },
        cvv: {
          selector: "#cvv",
        },
        expirationDate: {
          selector: "#expiration-date",
        },
      };

      // Only add postal code field for US
      if (countryCode === "US") {
        fields.postalCode = {
          selector: "#postal-code",
        };
      }

      const hostedFieldsInstance = await braintree.hostedFields.create({
        client: clientInstance,
        styles: {
          input: {
            "font-family": "Figtree-Regular, sans-serif, Helvetica, Verdana",
            color: "#9c9c9c",
            "font-size": "15px !important",
            "max-height": "100px",
          },
          "input.invalid": {
            color: "#d52b1e",
          },
          "input.valid": {
            color: "#333",
          },
        },
        fields: fields,
      });

      setHostedFieldsInstance(hostedFieldsInstance);

      hostedFieldsInstance.on("validityChange", (event) => {
        const field = event.fields[event.emittedBy];
        const newErrors = { ...errors };

        if (field.isValid) {
          delete newErrors[event.emittedBy];
        } else if (field.isPotentiallyValid) {
          delete newErrors[event.emittedBy];
        } else {
          const errorMessage = t(
            isSelfcareForm
              ? `selfcareFormFields.${event.emittedBy}_invalid`
              : `form_errors.${event.emittedBy}_invalid`,
            {
              defaultValue: `Invalid ${event.emittedBy}`,
              number: t(
                isSelfcareForm
                  ? "selfcareFormFields.card_number"
                  : "signup_page.card_number",
                "Card Number"
              ),
              expirationDate: t(
                isSelfcareForm
                  ? "selfcareFormFields.expiration_date"
                  : "signup_page.expiration_date",
                "Expiration Date"
              ),
              cvv: t(
                isSelfcareForm ? "selfcareFormFields.cvv" : "signup_page.cvv",
                "CVV"
              ),
              postalCode: t(
                isSelfcareForm
                  ? "selfcareFormFields.postal_code"
                  : "signup_page.postal_code",
                "Postal Code"
              ),
            }
          );
          newErrors[event.emittedBy] = errorMessage;
        }

        setErrors(newErrors);
        const formIsValid = Object.keys(event.fields).every(
          (key) => event.fields[key as keyof typeof event.fields].isValid
        );
        setIsValid(formIsValid);
        onValidityChange(formIsValid);
      });

      hostedFieldsInstance.on("focus", (event) => {
        setFieldStates((prev) => ({
          ...prev,
          [event.emittedBy]: {
            ...prev[event.emittedBy as keyof typeof prev],
            isFocused: true,
          },
        }));
      });

      hostedFieldsInstance.on("blur", (event) => {
        setFieldStates((prev) => ({
          ...prev,
          [event.emittedBy]: {
            ...prev[event.emittedBy as keyof typeof prev],
            isFocused: false,
          },
        }));
      });

      hostedFieldsInstance.on("empty", (event) => {
        setFieldStates((prev) => ({
          ...prev,
          [event.emittedBy]: {
            ...prev[event.emittedBy as keyof typeof prev],
            isEmpty: true,
          },
        }));
      });

      hostedFieldsInstance.on("notEmpty", (event) => {
        setFieldStates((prev) => ({
          ...prev,
          [event.emittedBy]: {
            ...prev[event.emittedBy as keyof typeof prev],
            isEmpty: false,
          },
        }));
      });
    } catch (error) {
      console.error("Error initializing Braintree:", error);
      setIsTearingDown(false);
    }
  };

  return (
    <Box data-test-id="braintree-hosted-fields-container">
      <FormControl
        fullWidth
        margin="dense"
        variant="outlined"
        data-test-id="card-number-container"
      >
        <InputLabel
          shrink={fieldStates.number.isFocused || !fieldStates.number.isEmpty}
          htmlFor="card-number"
          data-test-id="card-number-label"
        >
          {t(
            isSelfcareForm
              ? "selfcareFormFields.card_number"
              : "signup_page.card_number",
            {
              defaultValue: "Card Number",
            }
          )}
        </InputLabel>
        <Box
          id="card-number"
          className={styles.hostedField}
          data-test-id="card-number-field"
        />
        {errors.number && (
          <FormHelperText error data-test-id="card-number-error">
            {errors.number}
          </FormHelperText>
        )}
      </FormControl>

      <FormControl
        fullWidth
        margin="dense"
        variant="outlined"
        data-test-id="expiration-date-container"
      >
        <InputLabel
          shrink={
            fieldStates.expirationDate.isFocused ||
            !fieldStates.expirationDate.isEmpty
          }
          htmlFor="expiration-date"
          data-test-id="expiration-date-label"
        >
          {t(
            isSelfcareForm
              ? "selfcareFormFields.expiration_date"
              : "signup_page.expiration_date",
            {
              defaultValue: "Expiration Date",
            }
          )}
        </InputLabel>
        <Box
          id="expiration-date"
          className={styles.hostedField}
          data-test-id="expiration-date-field"
        />
        {errors.expirationDate && (
          <FormHelperText error data-test-id="expiration-date-error">
            {errors.expirationDate}
          </FormHelperText>
        )}
      </FormControl>

      <FormControl
        fullWidth
        margin="dense"
        variant="outlined"
        data-test-id="cvv-container"
      >
        <InputLabel
          shrink={fieldStates.cvv.isFocused || !fieldStates.cvv.isEmpty}
          htmlFor="cvv"
          data-test-id="cvv-label"
        >
          {t(isSelfcareForm ? "selfcareFormFields.cvv" : "signup_page.cvv", {
            defaultValue: "CVV",
          })}
        </InputLabel>
        <Box id="cvv" className={styles.hostedField} data-test-id="cvv-field" />
        {errors.cvv && (
          <FormHelperText error data-test-id="cvv-error">
            {errors.cvv}
          </FormHelperText>
        )}
      </FormControl>

      <Controller
        name="country_code"
        control={control}
        defaultValue={
          venue_id && BRAZIL_AIRPORT_VENUES.includes(venue_id.toUpperCase())
            ? "BR"
            : "US"
        }
        rules={{
          required: t(
            isSelfcareForm
              ? "selfcareFormFields.country_required"
              : "form_errors.country_required"
          ),
        }}
        render={({ field }) => (
          <FormControl
            fullWidth
            margin="dense"
            data-test-id="country-container"
          >
            <InputLabel id="country-select-label" data-test-id="country-label">
              {t(
                isSelfcareForm
                  ? "selfcareFormFields.country"
                  : "signup_page.country",
                {
                  defaultValue: "Country",
                }
              )}
            </InputLabel>
            <Select
              {...field}
              onChange={(e) => {
                if (!isTearingDown) {
                  field.onChange(e);
                  setCountryCode(e.target.value);
                }
              }}
              labelId="country-select-label"
              id="country-select"
              data-test-id="country-select"
              label={t(
                isSelfcareForm
                  ? "selfcareFormFields.country"
                  : "signup_page.country",
                {
                  defaultValue: "Country",
                }
              )}
              disabled={isTearingDown}
            >
              {COUNTRIES.map((country) => (
                <MenuItem
                  key={country.code}
                  value={country.code}
                  data-test-id={`country-option-${country.code.toLowerCase()}`}
                >
                  {country.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />

      {countryCode === "US" && (
        <FormControl
          fullWidth
          margin="dense"
          variant="outlined"
          data-test-id="postal-code-container"
        >
          <InputLabel
            shrink={
              fieldStates.postalCode.isFocused ||
              !fieldStates.postalCode.isEmpty
            }
            htmlFor="postal-code"
            data-test-id="postal-code-label"
          >
            {t(
              isSelfcareForm
                ? "selfcareFormFields.postal_code"
                : "signup_page.postal_code",
              {
                defaultValue: "Postal Code",
              }
            )}
          </InputLabel>
          <Box
            id="postal-code"
            className={styles.hostedField}
            data-test-id="postal-code-field"
          />
          {errors.postalCode && (
            <FormHelperText error data-test-id="postal-code-error">
              {t(
                isSelfcareForm
                  ? "selfcareFormFields.postal_code_invalid"
                  : "form_errors.postal_code_invalid",
                {
                  defaultValue: "Invalid postal code",
                }
              )}
            </FormHelperText>
          )}
        </FormControl>
      )}

      {countryCode === "BR" && (
        <Controller
          name="cpf"
          control={control}
          defaultValue=""
          rules={{
            required: t(
              isSelfcareForm
                ? "selfcareFormFields.cpf_required"
                : "form_errors.cpf_required",
              {
                defaultValue: "CPF is required",
              }
            ),
            validate: {
              validCPF: (value) =>
                validateCPF(value) ||
                t(
                  isSelfcareForm
                    ? "selfcareFormFields.cpf_invalid"
                    : "form_errors.cpf_invalid",
                  {
                    defaultValue: "Invalid CPF format",
                  }
                ),
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label={t(
                isSelfcareForm ? "selfcareFormFields.cpf" : "signup_page.cpf",
                {
                  defaultValue: "CPF",
                }
              )}
              fullWidth
              margin="dense"
              error={!!error}
              helperText={error?.message}
              inputProps={{
                maxLength: 14,
                "data-test-id": "cpf-input",
              }}
              onChange={(e) => {
                // Format CPF as user types: 123.456.789-00
                const formattedValue = formatCPF(e.target.value);
                field.onChange(formattedValue);
              }}
              data-test-id="cpf-field"
            />
          )}
        />
      )}

      {Object.keys(errors).length > 0 && (
        <Typography
          color="error"
          variant="body2"
          sx={{ mt: 2 }}
          data-test-id="form-error-message"
        >
          {t(
            isSelfcareForm
              ? "selfcareFormFields.correct_errors"
              : "signup_page.correct_errors",
            "Please correct the errors above"
          )}
        </Typography>
      )}
    </Box>
  );
});

BraintreeHostedFieldsForm.displayName = "BraintreeHostedFieldsForm";

export default BraintreeHostedFieldsForm;
